import React, {useEffect, useState} from 'react';
import {Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, Button, TextField} from '@mui/material';
import useAxiosInstance from "../useAxiosInstance";
import Swal from "sweetalert2";
import NavBar, {role} from "./NavBar";

const time = (t) => t?.replace("T", " ").slice(0, 19);
const DATE_LIMITS = {min: "2020-01-01", max: "2099-12-31"};

// One row per distinct crash (stack fingerprint); select one to see its recent reports, or find
// reports by unit and date. Times are UTC (server receive time) unless marked as device time.
export default function Crashes() {
    const instance = useAxiosInstance();
    const [signatures, setSignatures] = useState([]);
    const [selected, setSelected] = useState(null); // {title, query}
    const [reports, setReports] = useState([]);
    const [filter, setFilter] = useState({deviceId: "", from: "", to: ""});
    const [loading, setLoading] = useState(false);
    const isAdmin = role() === "admin";

    const loadSignatures = () => {
        setLoading(true);
        instance.get("Devices/CrashSignatures").then(setSignatures).catch(() => {}).finally(() => setLoading(false));
    };

    useEffect(loadSignatures, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadReports = (sel) => {
        setSelected(sel);
        setLoading(true);
        instance.get(`Devices/Crashes?${sel.query}`).then(setReports).catch(() => {}).finally(() => setLoading(false));
    };

    const open = (sig) => loadReports({title: sig.title, query: `signature=${sig.hash}`});

    const find = (e) => {
        e.preventDefault();
        const p = new URLSearchParams();
        if (filter.deviceId.trim()) p.append("deviceId", filter.deviceId.trim());
        if (filter.from) p.append("from", `${filter.from}T00:00:00Z`);
        if (filter.to) p.append("to", `${filter.to}T23:59:59Z`);
        const what = [filter.deviceId.trim() && `unit ${filter.deviceId.trim()}`,
            (filter.from || filter.to) && `${filter.from || "…"} to ${filter.to || "…"}`].filter(Boolean).join(", ");
        loadReports({title: `Reports${what ? ` for ${what}` : ""}`, query: p.toString()});
    };

    const confirm = (text) => Swal.fire({text, showCancelButton: true, confirmButtonText: "Delete", returnFocus: false})
        .then(r => r.isConfirmed);

    const removeSignature = (s) => confirm(`Delete the crash "${s.title}" and all ${s.count} report(s) of it? This can't be undone.`)
        .then(ok => {
            if (!ok) return;
            setLoading(true);
            instance.delete(`Devices/CrashSignatures/${s.hash}`).then(() => {
                if (selected?.query === `signature=${s.hash}`) { setSelected(null); setReports([]); }
                loadSignatures();
            }).catch(() => setLoading(false));
        });

    const removeReport = (r) => confirm(`Delete the report from unit ${r.deviceId} received ${time(r.createdAt)} UTC? The crash summary keeps its counts.`)
        .then(ok => {
            if (!ok) return;
            setLoading(true);
            instance.delete(`Devices/Crashes/${r.id}`).then(() => loadReports(selected)).catch(() => setLoading(false));
        });

    return (
        <div style={{margin: "auto", maxWidth: "1640px"}}>
            <NavBar title="Crashes." loading={loading}/>
            <TableContainer component={Paper} className="mx-3 mb-4" style={{width: "auto"}}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Crash</TableCell>
                            <TableCell align="right">Reports</TableCell>
                            <TableCell align="right">Units</TableCell>
                            <TableCell>App versions (lowest … highest)</TableCell>
                            <TableCell>First seen (UTC)</TableCell>
                            <TableCell>Last seen (UTC)</TableCell>
                            <TableCell/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {signatures.map(s => (
                            <TableRow key={s.hash} selected={selected?.query === `signature=${s.hash}`}>
                                <TableCell style={{maxWidth: 600, wordBreak: "break-word"}}>{s.title}</TableCell>
                                <TableCell align="right">{s.count}</TableCell>
                                <TableCell align="right">{s.deviceCount}</TableCell>
                                <TableCell>{s.firstAppVersion === s.lastAppVersion
                                    ? s.firstAppVersion || "-" : `${s.firstAppVersion || "-"} … ${s.lastAppVersion || "-"}`}</TableCell>
                                <TableCell>{time(s.firstSeen)}</TableCell>
                                <TableCell>{time(s.lastSeen)}</TableCell>
                                <TableCell className="text-nowrap">
                                    <Button size="small" onClick={() => open(s)}>Reports</Button>
                                    {isAdmin && <Button size="small" color="error" onClick={() => removeSignature(s)}>Delete</Button>}
                                </TableCell>
                            </TableRow>))}
                    </TableBody>
                </Table>
            </TableContainer>
            <form className="mx-3 mb-3 d-flex gap-2 align-items-center flex-wrap" onSubmit={find}>
                <TextField label="Unit" size="small" value={filter.deviceId}
                           onChange={e => setFilter({...filter, deviceId: e.target.value})}/>
                <TextField label="From (UTC)" type="date" size="small" InputLabelProps={{shrink: true}} inputProps={DATE_LIMITS}
                           value={filter.from} onChange={e => setFilter({...filter, from: e.target.value})}/>
                <TextField label="To (UTC)" type="date" size="small" InputLabelProps={{shrink: true}} inputProps={DATE_LIMITS}
                           value={filter.to} onChange={e => setFilter({...filter, to: e.target.value})}/>
                <Button type="submit" variant="outlined">Find reports</Button>
            </form>
            {selected && <div className="mx-3">
                <h5>{selected.title}</h5>
                <p className="text-muted">
                    {reports.length} report(s), newest first (at most 500). Received times are UTC; device time is the
                    unit's own clock when it crashed. Full reports are kept for 12 months; the crash summary for the
                    product's lifetime.
                </p>
                {reports.map(r => (
                    <details key={r.id} className="mb-2">
                        <summary>
                            received {time(r.createdAt)} UTC
                            {r.occurredAt && ` · device time ${time(r.occurredAt)}`}
                            {` · unit ${r.deviceId} · app ${r.appVersion || "-"} · OS ${r.osVersion || "-"} · hub ${r.hubVersion || "-"}`}
                            {!r.authenticated && " · unauthenticated"}
                            {isAdmin && <Button size="small" color="error" className="ms-2"
                                                onClick={(e) => { e.preventDefault(); removeReport(r); }}>Delete</Button>}
                        </summary>
                        <pre style={{whiteSpace: "pre-wrap", fontSize: 12, background: "#f6f6f6", padding: 8}}>{r.stack}</pre>
                    </details>))}
            </div>}
        </div>
    );
}
